import { useIntl } from "react-intl";
import { WmButton } from "@watermarkinsights/ripple-react";
import type {
  AgentMessage,
  TableSortByMessageId,
  TableSortState,
} from "../ai-chat-message-utils";
import { findSuggestedQuestionsForCurrentTurn } from "../ai-chat-message-utils";
import type { SourceProductConfig } from "../ai-chat-source-products";
import { ChatMessage } from "../messages/ChatMessage";
import { ChatIntroPanel } from "../messages/ChatIntroPanel";
import { AiSuggestedQuestions } from "../AiSuggestedQuestions";

interface ChatMessageListProps {
  isIntroState: boolean;
  visibleMessages: AgentMessage[];
  sourceProductsByMessageId: Record<string, SourceProductConfig[]>;
  tableSortByMessageId: TableSortByMessageId;
  onTableSortChange: (messageId: string, sort: TableSortState) => void;
  latestUserMessageId: string | null;
  isAwaitingAgentResponse: boolean;
  isRunning: boolean;
  canSend: boolean;
  shouldShowTypingIndicator: boolean;
  isJumpToLatestVisible: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  latestExchangeAnchorRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onScroll: () => void;
  onQuestionSelected: ((question: string) => void) | undefined;
  onJumpToLatest: () => void;
}

export const ChatMessageList = ({
  isIntroState,
  visibleMessages,
  sourceProductsByMessageId,
  tableSortByMessageId,
  onTableSortChange,
  latestUserMessageId,
  isAwaitingAgentResponse,
  isRunning,
  canSend,
  shouldShowTypingIndicator,
  isJumpToLatestVisible,
  messagesContainerRef,
  latestExchangeAnchorRef,
  messagesEndRef,
  onScroll,
  onQuestionSelected,
  onJumpToLatest,
}: ChatMessageListProps) => {
  const intl = useIntl();
  const currentTurnSuggestedQuestions =
    !isAwaitingAgentResponse && !isRunning && canSend
      ? findSuggestedQuestionsForCurrentTurn(visibleMessages)
      : null;

  return (
    <div className="tcw-messages-wrapper">
      <div className="tcw-messages" ref={messagesContainerRef} onScroll={onScroll}>
        {isIntroState && <ChatIntroPanel onQuestionSelected={onQuestionSelected} />}
        {visibleMessages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            sourceProducts={sourceProductsByMessageId[message.id]}
            tableSort={tableSortByMessageId[message.id]}
            onTableSortChange={(sort) => onTableSortChange(message.id, sort)}
            latestExchangeAnchorRef={
              message.id === latestUserMessageId ? latestExchangeAnchorRef : undefined
            }
          />
        ))}
        {shouldShowTypingIndicator && (
          <div className="tcw-message tcw-message--assistant--typing">
            <img
              src="https://cdn.watermarkinsights.com/images/loaders/ai-pulsing-dot-loader.svg"
              alt="Loading"
              className="tcw-loading-loader"
            />{" "}
            {intl.formatMessage({ id: "app.toolboxChat.typing.thinking" })}
          </div>
        )}
        {currentTurnSuggestedQuestions && (
          <div className="tcw-message tcw-message--suggestions">
            <AiSuggestedQuestions
              label={currentTurnSuggestedQuestions.label}
              questions={currentTurnSuggestedQuestions.questions}
              onQuestionSelected={onQuestionSelected}
            />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {isJumpToLatestVisible && (
        <WmButton
          button-type="icononly"
          tooltip={intl.formatMessage({ id: "app.toolboxChat.action.jumpToLatest" })}
          tooltip-position="top"
          icon="#move-down"
          icon-size="18px"
          className="tcw-jump-to-latest-btn"
          onClick={onJumpToLatest}
        />
      )}
    </div>
  );
};
